export enum PriceListType {
  STANDARD = 'standard',
  CUSTOMER_SPECIFIC = 'customer_specific',
  PROMOTIONAL = 'promotional',
  QUANTITY_BASED = 'quantity_based',
}

export enum PriceListStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
  EXPIRED = 'expired',
}

export enum PriceType {
  FIXED = 'fixed',
  PERCENTAGE_MARKUP = 'percentage_markup',
  PERCENTAGE_DISCOUNT = 'percentage_discount',
}

export interface PriceListItem {
  id: string;
  productId: string;
  uomId?: string;
  priceType: PriceType;
  basePrice: number;
  percentage?: number;
  finalPrice: number;
  minimumQuantity?: number;
  maximumQuantity?: number;
  additionalDiscountPercent?: number;
  costPrice?: number;
  markupPercent?: number;
  validFrom?: string;
  validTo?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  product?: any;
  uom?: any;
}

export interface PriceList {
  id: string;
  code: string;
  name: string;
  description: string;
  type: PriceListType;
  status: PriceListStatus;
  companyId?: string;
  validFrom: string;
  validTo?: string;
  currency?: string;
  customerId?: string;
  priority: number;
  globalDiscountPercent?: number;
  minimumOrderValue?: number;
  autoApply: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  items: PriceListItem[];
  company?: any;
  customer?: any;
}

export interface CreatePriceListItemData {
  productId: string;
  uomId?: string;
  priceType: PriceType;
  basePrice: number;
  percentage?: number;
  // Required for fixed prices; the server calculates it for the percentage types
  finalPrice?: number;
  minimumQuantity?: number;
  maximumQuantity?: number;
  additionalDiscountPercent?: number;
  costPrice?: number;
  markupPercent?: number;
  validFrom?: string;
  validTo?: string;
  notes?: string;
}

export interface CreatePriceListData {
  code: string;
  name: string;
  description: string;
  type: PriceListType;
  status?: PriceListStatus;
  companyId?: string;
  validFrom: string;
  validTo?: string;
  currency?: string;
  customerId?: string;
  priority?: number;
  globalDiscountPercent?: number;
  minimumOrderValue?: number;
  autoApply?: boolean;
  items: CreatePriceListItemData[];
}

// Null clears the end date or the customer on update
export interface UpdatePriceListData extends Partial<Omit<CreatePriceListData, 'validTo' | 'customerId'>> {
  validTo?: string | null;
  customerId?: string | null;
}

export interface PriceListFilters {
  search?: string;
  type?: string;
  status?: string;
  customerId?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface PriceListResponse {
  data: PriceList[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PriceListSummary {
  total: number;
  active: number;
  draft: number;
  inactive: number;
  expired: number;
}

export interface ProductPriceDetails {
  basePrice: number;
  finalPrice: number;
  discountPercent: number;
  priceListId?: string;
  priceListName?: string;
}

export interface ProductPriceRequest {
  productId: string;
  customerId?: string;
  quantity?: number;
}

export interface ProductPriceResponse {
  productId: string;
  customerId?: string;
  quantity: number;
  price: number;
}